import { PaginatedList, PaginatedQueryParams } from "../types/pagination";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";

// Document interfaces based on actual backend DTOs
export interface DocumentResponseDto {
    id: number;
    blobName: string;
    originalFileName: string;
    url: string;
    fileSizeBytes: number;
    contentType: string;
    uploadedAt: string;
    entityType: string;
    entityId: number;
    documentCategory?: string;
    description?: string;
    isActive: boolean;
    isPermit: boolean;
    expirationDate?: string;
    uploadedByUserId?: number;
    createdAt: string;
    updatedAt?: string;
    sortOrder: number;
}

export interface DocumentListItemDto extends DocumentResponseDto {
    entityName: string;
}

export interface GetDocumentsByEntityRequest {
    entityType: string;
    entityId: number;
    documentCategory?: string;
    includeInactive?: boolean;
}

export interface DocumentUploadRequestDto {
    file: File;
    entityType: string;
    entityId: number;
    documentCategory?: string;
    description?: string;
    isPermit?: boolean;
    isActive?: boolean;
    expirationDate?: string;
}

export interface DocumentUploadResponseDto {
    id: number;
    blobName: string;
    originalFileName: string;
    url: string;
    fileSizeBytes: number;
    contentType: string;
    entityType: string;
    entityId: number;
    documentCategory?: string;
    description?: string;
    isPermit: boolean;
    isActive: boolean;
    expirationDate?: string;
    uploadedAt: string;
}

export interface ExpiringDocumentDto {
    id: number;
    entityType: string;
    entityId: number;
    entityName: string;
    originalFileName: string;
    documentCategory?: string;
    expirationDate: string;
    daysUntilExpiration: number;
    isExpired: boolean;
    isPermit: boolean;
    url: string;
}

export interface EntityExpiringDocumentsDto {
    entityType: string;
    entityId: number;
    entityName: string;
    documentCount: number;
    earliestExpiration: string;
    documents: ExpiringDocumentDto[];
}

export interface ExpiringDocumentsParams extends PaginatedQueryParams {
    entityType?: string;
    documentCategory?: string;
    search?: string;
    status?: string;
}

export interface DocumentsListParams extends PaginatedQueryParams {
    entityType: string;
    entityId?: number;
    documentCategory?: string;
    search?: string;
    status?: string;
    isPermit?: boolean;
}

export interface ReorderDocumentsRequest {
    entityType: string;
    entityId: number;
    orderedDocumentIds: number[];
}

export interface ReplaceDocumentRequest {
    id: number;
    file?: File | null;
    documentCategory?: string;
    description?: string;
    isPermit?: boolean;
    isActive?: boolean;
    expirationDate?: string;
}

export const documentsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getDocumentsByEntity: builder.query<
            DocumentResponseDto[],
            GetDocumentsByEntityRequest
        >({
            query: ({ entityType, entityId, documentCategory, includeInactive }) => ({
                url: `documents/${entityType}/${entityId}`,
                params: {
                    ...(documentCategory && { documentCategory }),
                    ...(includeInactive !== undefined && { includeInactive }),
                },
            }),
            providesTags: (result, error, { entityType, entityId }) => [
                { type: "Documents", id: `${entityType}-${entityId}` },
                "Documents",
            ],
            transformResponse: unwrapApiResponse,
        }),

        getDocumentById: builder.query<DocumentResponseDto, number>({
            query: (id) => `documents/${id}`,
            providesTags: (result, error, id) => [{ type: "Documents", id }],
            transformResponse: unwrapApiResponse,
        }),

        uploadDocument: builder.mutation<
            DocumentUploadResponseDto,
            {
                file: File;
                entityType: string;
                entityId: number;
                documentCategory?: string;
                description?: string;
                isPermit?: boolean;
                isActive?: boolean;
                expirationDate?: string;
            }
        >({
            query: ({ file, entityType, entityId, documentCategory, description, isPermit, isActive, expirationDate }) => {
                const formData = new FormData();

                // Append file
                formData.append('File', file);

                // Append all other fields as form data (matching DocumentUploadFormDto structure)
                formData.append('EntityType', entityType);
                formData.append('EntityId', entityId.toString());

                if (documentCategory) {
                    formData.append('DocumentCategory', documentCategory);
                }
                if (description) {
                    formData.append('Description', description);
                }
                if (isPermit !== undefined) {
                    formData.append('IsPermit', isPermit.toString());
                }
                if (isActive !== undefined) {
                    formData.append('IsActive', isActive.toString());
                }
                if (expirationDate) {
                    formData.append('ExpirationDate', expirationDate);
                }

                console.log('Upload URL: documents/upload');
                console.log('EntityType:', entityType);
                console.log('EntityId:', entityId);
                console.log('IsPermit:', isPermit);
                console.log('IsActive:', isActive);
                console.log('ExpirationDate:', expirationDate);
                console.log('FormData file:', file.name);

                return {
                    url: 'documents/upload',
                    method: "POST",
                    body: formData,
                };
            },
            invalidatesTags: (result, error, { entityType, entityId }) => [
                { type: "Documents", id: `${entityType}-${entityId}` },
                "Documents",
            ],
            transformResponse: unwrapApiResponse,
        }),

        deleteDocument: builder.mutation<void, number>({
            query: (id) => ({
                url: `documents/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, id) => [
                { type: "Documents", id },
                "Documents",
            ],
        }),

        reorderDocuments: builder.mutation<void, ReorderDocumentsRequest>({
            query: (body) => ({
                url: "documents/reorder",
                method: "PUT",
                body,
            }),
            invalidatesTags: (result, error, { entityType, entityId }) => [
                { type: "Documents", id: `${entityType}-${entityId}` },
                "Documents",
            ],
        }),

        replaceDocument: builder.mutation<DocumentResponseDto, ReplaceDocumentRequest>({
            query: ({ id, file, documentCategory, description, isPermit, isActive, expirationDate }) => {
                const formData = new FormData();

                if (file) {
                    formData.append("File", file);
                }
                formData.append("DocumentCategory", documentCategory ?? "");
                formData.append("Description", description ?? "");
                if (isPermit !== undefined) {
                    formData.append("IsPermit", isPermit.toString());
                }
                if (isActive !== undefined) {
                    formData.append("IsActive", isActive.toString());
                }
                formData.append("ExpirationDate", expirationDate ?? "");

                return {
                    url: `documents/${id}/replace`,
                    method: "PUT",
                    body: formData,
                };
            },
            invalidatesTags: (result, error, { id }) => [
                { type: "Documents", id },
                ...(result
                    ? [{ type: "Documents" as const, id: `${result.entityType}-${result.entityId}` }]
                    : []),
                "Documents",
            ],
            transformResponse: unwrapApiResponse,
        }),

        getDocumentsList: builder.query<
            PaginatedList<DocumentListItemDto>,
            DocumentsListParams
        >({
            query: ({ pageNumber, pageSize, entityType, entityId, documentCategory, search, status, isPermit }) => ({
                url: "documents/list",
                params: {
                    PageNumber: pageNumber,
                    PageSize: pageSize,
                    entityType,
                    ...(entityId !== undefined && { entityId }),
                    ...(documentCategory && { documentCategory }),
                    ...(search && { search }),
                    ...(status && { status }),
                    ...(isPermit !== undefined && { isPermit }),
                },
            }),
            providesTags: ["Documents"],
            transformResponse: unwrapApiResponse,
        }),

        getExpiringDocuments: builder.query<
            PaginatedList<EntityExpiringDocumentsDto>,
            ExpiringDocumentsParams
        >({
            query: ({ pageNumber, pageSize, entityType, documentCategory, search, status }) => ({
                url: "documents/expiring",
                params: {
                    PageNumber: pageNumber,
                    PageSize: pageSize,
                    ...(entityType && { entityType }),
                    ...(documentCategory && { documentCategory }),
                    ...(search && { search }),
                    ...(status && { status }),
                },
            }),
            providesTags: ["Documents"],
            transformResponse: unwrapApiResponse,
        }),

        getDocumentsByEntityWithAuth: builder.query<
            DocumentResponseDto[],
            GetDocumentsByEntityRequest & { ssn: string }
        >({
            query: ({ entityType, entityId, ssn, documentCategory, includeInactive }) => ({
                url: `documents/${entityType}/${entityId}/auth`,
                params: {
                    ssn,
                    ...(documentCategory && { documentCategory }),
                    ...(includeInactive !== undefined && { includeInactive }),
                },
            }),
            providesTags: (result, error, { entityType, entityId }) => [
                { type: "Documents", id: `${entityType}-${entityId}` },
                "Documents",
            ],
            transformResponse: unwrapApiResponse,
        }),
    }),
});

export const {
    useGetDocumentsByEntityQuery,
    useGetDocumentByIdQuery,
    useUploadDocumentMutation,
    useDeleteDocumentMutation,
    useReorderDocumentsMutation,
    useReplaceDocumentMutation,
    useGetDocumentsListQuery,
    useGetDocumentsByEntityWithAuthQuery,
    useGetExpiringDocumentsQuery,
} = documentsApi; 